import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { 
  FaEdit, 
  FaTrash, 
  FaCheck, 
  FaTimes, 
  FaUserCog, 
  FaUserShield, 
  FaUser, 
  FaToggleOn, 
  FaToggleOff,
  FaKey,
  FaSave,
  FaTimesCircle
} from 'react-icons/fa';
import './UserManagement.css';

function UserManagement({ addToast }) {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://airexheater.in/api/admin/auth/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users);
      } else {
        addToast(data.message || 'Failed to fetch users', 'error');
      }
    } catch (error) {
      addToast('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      const response = await fetch(`https://airexheater.in/api/admin/auth/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role })
      });
      const data = await response.json();
      if (response.ok) {
        addToast(data.message, 'success');
        fetchUsers();
      } else {
        addToast(data.message, 'error');
      }
    } catch (error) {
      addToast('Error updating role', 'error');
    }
    setEditingRole(null);
  };

  const updateEditPermission = async (userId, canEdit) => {
    try {
      const response = await fetch(`https://airexheater.in/api/admin/auth/users/${userId}/edit-permission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ canEdit })
      });
      const data = await response.json();
      if (response.ok) {
        addToast(data.message, 'success');
        fetchUsers();
      } else {
        addToast(data.message, 'error');
      }
    } catch (error) {
      addToast('Error updating permission', 'error');
    }
  };

  const toggleUserStatus = async (userId, isActive) => {
    try {
      const response = await fetch(`https://airexheater.in/api/admin/auth/users/${userId}/toggle-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !isActive })
      });
      const data = await response.json();
      if (response.ok) {
        addToast(data.message, 'success');
        fetchUsers();
      } else {
        addToast(data.message, 'error');
      }
    } catch (error) {
      addToast('Error updating user status', 'error');
    }
  };

  // Change password for any user (Super Admin only)
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      addToast('Passwords do not match', 'error');
      return;
    }
    
    if (newPassword.length < 6) {
      addToast('Password must be at least 6 characters', 'error');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const response = await fetch(`https://airexheater.in/api/admin/auth/admin/change-password/${selectedUser._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword })
      });
      
      const data = await response.json();
      if (response.ok) {
        addToast(`Password changed for ${selectedUser.name} successfully`, 'success');
        setShowPasswordModal(false);
        setSelectedUser(null);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        addToast(data.message || 'Failed to change password', 'error');
      }
    } catch (error) {
      addToast('Error changing password', 'error');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Delete user function
  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;
    
    if (deleteConfirm._id === currentUser._id) {
      addToast('You cannot delete your own account', 'error');
      setDeleteConfirm(null);
      return;
    }
    
    if (deleteConfirm.role === 'super_admin') {
      addToast('Cannot delete Super Admin users', 'error');
      setDeleteConfirm(null);
      return;
    }
    
    try {
      const response = await fetch(`https://airexheater.in/api/admin/auth/users/${deleteConfirm._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        addToast(`User "${deleteConfirm.name}" deleted successfully`, 'success');
        fetchUsers();
      } else {
        addToast(data.message || 'Failed to delete user', 'error');
      }
    } catch (error) {
      addToast('Error deleting user', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const canDeleteUser = (user) => {
    return currentUser.role === 'super_admin' && 
           currentUser._id !== user._id && 
           user.role !== 'super_admin';
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'super_admin':
        return <FaUserCog className="role-icon super-admin" />;
      case 'admin':
        return <FaUserShield className="role-icon admin" />;
      default:
        return <FaUser className="role-icon user" />;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'super_admin':
        return 'role-badge super-admin';
      case 'admin':
        return 'role-badge admin';
      default:
        return 'role-badge user';
    }
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="um-header">
        <div>
          <h2>User Management</h2>
          <p className="um-subtitle">Manage user roles, permissions, and passwords</p>
        </div>
        <div className="um-stats">
          <div className="stat-badge">
            <FaUserCog /> Super Admins: {users.filter(u => u.role === 'super_admin').length}
          </div>
          <div className="stat-badge">
            <FaUserShield /> Admins: {users.filter(u => u.role === 'admin').length}
          </div>
          <div className="stat-badge">
            <FaUser /> Users: {users.filter(u => u.role === 'user').length}
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="um-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Edit Permission</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className={!user.isActive ? 'inactive-user' : ''}>
                <td className="user-cell">
                  <div className="user-info">
                    <div className="user-avatar">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-details">
                      <strong>{user.name}</strong>
                      <small>ID: {user._id.slice(-8)}</small>
                    </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <div className="role-container">
                    {getRoleIcon(user.role)}
                    {editingRole === user._id ? (
                      <select 
                        value={selectedRole} 
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="role-select"
                        autoFocus
                      >
                        <option value="super_admin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </select>
                    ) : (
                      <span className={getRoleBadgeClass(user.role)}>
                        {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {user.role === 'user' ? (
                    <button 
                      className={`permission-toggle ${user.canEdit ? 'enabled' : 'disabled'}`}
                      onClick={() => updateEditPermission(user._id, !user.canEdit)}
                      disabled={currentUser._id === user._id}
                      title={currentUser._id === user._id ? "Cannot modify your own permission" : (user.canEdit ? "Revoke edit permission" : "Grant edit permission")}
                    >
                      {user.canEdit ? <FaCheck /> : <FaTimes />}
                      {user.canEdit ? ' Can Edit' : ' Read Only'}
                    </button>
                  ) : (
                    <span className="permission-na">—</span>
                  )}
                </td>
                <td>
                  <button 
                    className={`status-toggle ${user.isActive ? 'active' : 'inactive'}`}
                    onClick={() => toggleUserStatus(user._id, user.isActive)}
                    disabled={currentUser._id === user._id}
                    title={currentUser._id === user._id ? "Cannot modify your own status" : (user.isActive ? "Deactivate user" : "Activate user")}
                  >
                    {user.isActive ? <FaToggleOn /> : <FaToggleOff />}
                    {user.isActive ? ' Active' : ' Inactive'}
                  </button>
                </td>
                <td className="actions-cell">
                  {currentUser.role === 'super_admin' && currentUser._id !== user._id && (
                    <>
                      {/* Change Role Button (only for non-super_admin users) */}
                      {user.role !== 'super_admin' && (
                        <button 
                          className="action-role"
                          onClick={() => {
                            setEditingRole(user._id);
                            setSelectedRole(user.role);
                          }}
                          title="Change Role"
                        >
                          <FaUserCog /> Role
                        </button>
                      )}
                      
                      {/* Reset Password Button - Super Admin only */}
                      <button 
                        className="action-password"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordModal(true);
                        }}
                        title="Reset Password"
                      >
                        <FaKey /> Reset Password
                      </button>
                      
                      {/* Delete Button */}
                      {canDeleteUser(user) && (
                        <button 
                          className="action-delete"
                          onClick={() => setDeleteConfirm(user)}
                          title="Delete User"
                        >
                          <FaTrash /> Delete
                        </button>
                      )}
                      
                      {/* Role Actions */}
                      {editingRole === user._id && (
                        <div className="role-actions">
                          <button 
                            className="save-role"
                            onClick={() => updateUserRole(user._id, selectedRole)}
                          >
                            <FaCheck /> Save
                          </button>
                          <button 
                            className="cancel-role"
                            onClick={() => setEditingRole(null)}
                          >
                            <FaTimes /> Cancel
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="no-users">
          <p>No users found</p>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header password-header">
              <h2><FaKey /> Reset Password</h2>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="password-user-info">
                <div className="user-avatar-large">
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <div className="user-info-text">
                  <h3>{selectedUser.name}</h3>
                  <p>{selectedUser.email}</p>
                  <span className={`role-badge ${selectedUser.role}`}>
                    {selectedUser.role === 'super_admin' ? 'Super Admin' : selectedUser.role === 'admin' ? 'Admin' : 'User'}
                  </span>
                </div>
              </div>
              
              <form onSubmit={handleChangePassword} className="password-form">
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowPasswordModal(false)}>
                    <FaTimesCircle /> Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={passwordLoading}>
                    <FaSave /> {passwordLoading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h2>Confirm Delete</h2>
              <button className="close-btn" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this user?</p>
              <div className="delete-preview">
                <strong>Name:</strong> {deleteConfirm.name}
                <br />
                <strong>Email:</strong> {deleteConfirm.email}
                <br />
                <strong>Role:</strong> {deleteConfirm.role === 'admin' ? 'Admin' : 'User'}
              </div>
              <div className="delete-actions">
                <button className="cancel-btn" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button className="confirm-delete-btn" onClick={handleDeleteUser}>
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;